<template>
  <p>
    Utiliza tu API Key integrar tus datos de {{ ProductName }} con otras aplicaciones. Esta
    información es sensible y brinda acceso a datos de tu empresa a través de API's activas. Si se
    compromete, puedes restablecerlo, pero las aplicaciones que usen el código anterior perderán
    acceso. <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer"> Click aquí </a> para
    conocer más.
  </p>

  <a :href="apiDocumentationUrl" target="_blank" rel="noopener noreferrer">
    <span>
      Ver documentación del API
      <img :src="externalLinkImage" alt="external-link" />
    </span>
  </a>

  <div class="key__wrapper">
    <label>API Key</label>

    <div class="input__wrapper">
      <div class="input">
        <a-input-password :value="apiKey" :disabled="!apiKey" placeholder="Genere su api key" />
        <a-tooltip title="Copiar">
          <a-button :disabled="!apiKey" @click="handleCopy(apiKey)">
            <template #icon>
              <CopyOutlined />
            </template>
          </a-button>
        </a-tooltip>
      </div>

      <div class="action">
        <a-button class="button" @click="generateApiKey">Generar API Key</a-button>
      </div>
    </div>
  </div>

  <RestrictionChangePlanModal
    title="Obtén esta funcionalidad personalizando tu plan."
    v-if="openRestriction"
    v-model:open="openRestriction"
    :planRequest="dataPlan.plan"
    showAlert
  >
    <template #body>
      <p>
        Tu plan {{ PlanTypeText[dataPlan.plan as PlanType] }} no incluye esta funcionalidad.<br/>
        Solicita personalizar tu plan para potenciar tu ambiente de trabajo.
      </p>
      <p>
        {{
          isClientBeexcc 
          ? "Cuenta con nosotros para elevar tu experiencia. Estamos listos para asistirte. ✨"
          : "Aprovecha al máximo todo lo que puedes lograr con BeexCC. ¡Lleva tu experiencia más lejos! ✨"
        }}
      </p>
    </template>
  </RestrictionChangePlanModal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { CopyOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";

import { useCopyToClipboard } from "@/app/shared/composables/useCopyToClipboard";
import { apiService } from "@/app/platformSettings/services/ApiService";
import { ProductName } from "@/app/shared/utils/constants";

import externalLinkImage from "@/app/supervision/assets/external-link.svg?url";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { PlanType, PlanTypeText } from "@/app/common/types";
import { useStore } from "vuex";

const store = useStore();
const DOCS_URL = `${
  import.meta.env.VITE_WEB_DOC
}/api-key-callback/`;

const apiDocumentationUrl = `https://api.${import.meta.env.VITE_HOSTNAME}/docs`;

const props = defineProps<{
  token: string;
  dataPlan?: any;
}>();

const apiKey = ref(props.token);
const loading = ref(false);
const openRestriction = ref(false);

const handleCopy = useCopyToClipboard();

const restrictionBeexApi = computed(() => {
  return props.dataPlan && !props.dataPlan?.configuration?.beex_api?.active;
});
const user = computed(() => store.getters["getDataUser"]);
const isClientBeexcc = computed(() => user.value?.is_beexcc);
const generateApiKey = async () => {
  if (restrictionBeexApi.value) {
    openRestriction.value = true;
    return;
  }
  loading.value = true;
  try {
    apiKey.value = await apiService.generateToken();
  } catch (error) {
    message.error("No se pudo generar el token");
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped lang="sass">
p
  color: $gray-8
  margin-bottom: 16px

a
  display: inline-block

  span
    display: flex
    align-items: center

  img
    margin-left: 5px

.key__wrapper
  margin-top: 16px

.input__wrapper
  display: flex
  align-items: center
  gap: 12px

.input
  width: 100%
  max-width: min(450px, 100%)
  display: flex !important
  gap: 4px

.action
  width: 170px

  .button
    width: 100%
</style>
